import { FontWeights, IRawStyle, ITheme, mergeStyles, useTheme } from '@fluentui/react';
import { IPNavStyles } from 'baseComponents/Nav/IPNav';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPText } from 'baseComponents/Text/IPText';
import { globalNavRowHeight } from 'globalConstants';

//
// Styles and small presentation parts shared by the rail (NavMain.tsx), the callout beside a pinned
// group header (NavHintCallout.tsx) and the panel hosts.
//

// Marks an entry that can expand. Fluent draws the chevron of a nested group on the left, where the
// icon is; the selectors below move it to the right edge for rows with this class.
export const navExpandableClass = 'isoplanner-nav-expandable';

// The root of the menu. The callout uses it to tell an event inside the menu from one outside it.
export const navRailClass = 'isoplanner-nav-main-rail';

// Space above the first entry and between groups.
export const navEdgeSpacing = 10;

// A caption occupies the same box whether it shows its text or, in the compact rail, a rule, so
// every entry sits at the same height in the rail and in the flyout that opens over it.
const navCaptionPaddingTop = 12;
const navCaptionPaddingBottom = 4;
const navCaptionLineHeight = 16;
const navCaptionPadding = `${navCaptionPaddingTop}px 8px ${navCaptionPaddingBottom}px 10px`;

// The look of a caption. Shared by the plain caption of a fixed group and by the button Fluent
// draws for a group the user can close, so the two are the same row.
const buildNavCaptionTextStyle = (theme: ITheme): IRawStyle => ({
  color: theme.semanticColors.bodySubtext,
  fontWeight: FontWeights.semibold,
  padding: navCaptionPadding,
  lineHeight: `${navCaptionLineHeight}px`,
});

// The compact rail is 43px of icons; it hides its scrollbar. The wheel and the keyboard still reach
// every entry.
export const hiddenScrollbar = {
  scrollbarWidth: 'none' as const,
  selectors: { '::-webkit-scrollbar': { display: 'none' } },
};

// The menu's own scrollbar is slimmer and paler than the app's: beside a column of 40px rows the
// default 11px bar reads as a second border. The thumb is a translucent neutral rather than a fixed
// grey, so the one value suits the light menu and the dark one.
const navScrollbarWidth = 8;
const navScrollbarThumb = 'rgba(128, 128, 128, 0.4)';
const navScrollbarThumbHover = 'rgba(128, 128, 128, 0.7)';

// Transparent border plus content-box clipping insets the thumb inside its track, so it reads as a
// slim pill instead of filling the full width of the bar.
const navScrollbarThumbShape: IRawStyle = {
  borderRadius: navScrollbarWidth / 2,
  border: '2px solid transparent',
  backgroundClip: 'content-box',
};

// Both states declare the same width. A classic scrollbar takes layout space, so a width that
// changed with the state would reflow the menu every time the pointer arrived. The thumb and its
// hovered shade take their colours from two properties registered in index.css.
const navScrollbarThumbVar = 'var(--nav-scrollbar-thumb)';
const navScrollbarThumbHoverVar = 'var(--nav-scrollbar-thumb-hover)';
const navScrollbarFrame = {
  '::-webkit-scrollbar': { width: navScrollbarWidth, height: navScrollbarWidth },
  '::-webkit-scrollbar-track': { background: 'transparent' },
  '::-webkit-scrollbar-thumb': { ...navScrollbarThumbShape, background: navScrollbarThumbVar },
  '::-webkit-scrollbar-thumb:hover': { ...navScrollbarThumbShape, background: navScrollbarThumbHoverVar },
};

// The full menu's scrollbar thumb fades in when the pointer or the keyboard focus reaches the menu
// and fades out when they leave. The two classes below are swapped on the scroller itself, and the
// fade is a transition of the two registered properties on that scroller, not on the thumb:
// Chromium ignores a transition on ::-webkit-scrollbar-thumb, but it restyles the scrollbar parts
// whenever the scroller's own computed style changes, and a transitioning registered property
// changes it on every frame. The thumb picks up each interpolated value through var(). A transition
// takes its duration from the state it moves into, so the two durations may differ.
//
// The hovered shade fades along with the thumb on purpose. A pointer coming in from the page
// crosses the bar before it reaches the rows and hovers the thumb on the way; a fixed hover colour
// there painted the pill at full strength for a frame and then dropped it back to the fade.
//
// scrollbar-color and scrollbar-width stay out: a non-auto value makes Chrome ignore the
// ::-webkit-scrollbar rules above.
const navScrollbarFadeIn = '300ms';
const navScrollbarFadeOut = '600ms';
const navScrollbarTransition = (duration: string): string =>
  `--nav-scrollbar-thumb ${duration} ease, --nav-scrollbar-thumb-hover ${duration} ease`;

export const navScrollerThumbHidden = mergeStyles({
  // A device without a hovering pointer keeps its thumb: nothing would ever reveal a hidden one.
  ['--nav-scrollbar-thumb' as string]: navScrollbarThumb,
  ['--nav-scrollbar-thumb-hover' as string]: navScrollbarThumbHover,
  transition: navScrollbarTransition(navScrollbarFadeOut),
  selectors: {
    ...navScrollbarFrame,
    '@media (hover: hover)': {
      ['--nav-scrollbar-thumb' as string]: 'transparent',
      ['--nav-scrollbar-thumb-hover' as string]: 'transparent',
    },
  },
});

export const navScrollerThumbShown = mergeStyles({
  ['--nav-scrollbar-thumb' as string]: navScrollbarThumb,
  ['--nav-scrollbar-thumb-hover' as string]: navScrollbarThumbHover,
  transition: navScrollbarTransition(navScrollbarFadeIn),
  selectors: {
    ...navScrollbarFrame,
  },
});

/** The selectors on a menu root. `compact` is the icon-only rail. */
export const buildNavRootSelectors = (theme: ITheme, compact: boolean): Record<string, IRawStyle> => ({
  // The chevron of an expandable entry moves to the right edge, clear of the icon.
  [`.ms-Nav-compositeLink:has(.${navExpandableClass}) .ms-Nav-chevronButton`]: {
    left: 'auto',
    right: 4,
    display: compact ? 'none' : undefined,
  },
  // A short label keeps clear of the chevron.
  [`.ms-Nav-compositeLink:has(.${navExpandableClass}) .ms-Nav-link`]: {
    paddingRight: compact ? undefined : 32,
  },
  // Nested entries without an icon (task views, active projects) would keep Fluent's nested
  // indent, which leaves too big a gap; the compact rail draws a dot for each in the icon column.
  '.ms-Nav-navItem:has(.isoplanner-nav-main-tasks, .isoplanner-nav-main-projects) .ms-Nav-navItems .ms-Nav-link': {
    paddingLeft: compact ? 10 : 16,
  },
  // Tint the children of an open entry. Only nested lists match.
  '.ms-Nav-navItem .ms-Nav-navItems': {
    backgroundColor: theme.semanticColors.navSubItemBackground,
  },
  // A group the user can close is drawn by Fluent's own group header, a button with a chevron. The
  // two rules below make that button the caption. Only a group header matches: the chevron of an
  // entry sits inside .ms-Nav-compositeLink, a level deeper.
  '.ms-Nav-group > .ms-Nav-chevronButton': {
    ...buildNavCaptionTextStyle(theme),
    // Fluent gives a group header a large font, a fixed row height and a bottom border.
    fontSize: theme.fonts.small.fontSize,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    height: 'auto',
    margin: 0,
    border: 'none',
    // The caption keeps to one line: the compact rail draws a rule of one fixed height in the same
    // slot, and a caption of two lines would put every row below it out of step with the flyout that
    // opens over the rail. Nothing is clipped, so the chevron and the focus ring stay whole.
    whiteSpace: 'nowrap',
    overflow: 'visible',
    selectors: {
      ':hover': {
        color: theme.semanticColors.bodyText,
      },
    },
  },
  '.ms-Nav-group > .ms-Nav-chevronButton .ms-Nav-chevron': {
    // Fluent pins the chevron to the left edge, before the words. Here it follows them: the right
    // edge of a caption is where the chevrons of the entries under it already sit, and two levels of
    // chevron in one column read as one list.
    position: 'static',
    order: 1,
    flexShrink: 0,
    height: 'auto',
    lineHeight: 'normal',
    fontSize: 10,
    paddingLeft: 0,
  },
});

/** The Nav styles. Fluent hard-codes its row height in three slots; all three follow globalNavRowHeight. */
export const buildNavStyles = (theme: ITheme, compact: boolean): Partial<IPNavStyles> => ({
  root: {
    boxSizing: 'border-box',
    selectors: buildNavRootSelectors(theme, compact),
  },
  link: {
    paddingLeft: compact ? 10 : undefined,
    // A long label wraps to two lines in the full menu. The compact rail never wraps: its labels are
    // out of sight, and a second line would push the icons below out of step with the flyout.
    whiteSpace: compact ? 'nowrap' : 'normal',
    lineHeight: 'inherit',
    height: 'auto',
    minHeight: globalNavRowHeight,
  },
  linkText: {
    paddingLeft: compact ? 10 : undefined,
  },
  chevronButton: {
    height: globalNavRowHeight - 2,
    lineHeight: `${globalNavRowHeight}px`,
  },
  chevronIcon: {
    height: globalNavRowHeight,
    lineHeight: `${globalNavRowHeight}px`,
    paddingLeft: compact ? 6 : undefined,
  },
  groupContent: {
    marginBottom: navEdgeSpacing,
  },
});

/** The panel styles of the flyout and the mobile panel: the menu fills the panel and scrolls itself. */
export const navPanelStyles = {
  scrollableContent: { height: '100%', flexGrow: 1, minHeight: 0, overflow: 'hidden' as const },
  content: { padding: 0, height: '100%' },
};

interface INavGroupCaptionProps {
  text: string;
}

/** The caption above a group, in the full menu and in the callout. */
export const NavGroupCaption = (props: INavGroupCaptionProps) => {
  const theme = useTheme();

  return (
    <IPText block variant="small" styles={{ root: buildNavCaptionTextStyle(theme) }}>
      {props.text}
    </IPText>
  );
};

/** The compact rail's stand-in for a caption: a rule in the caption's box, so the rows keep their height. */
export const NavGroupRule = () => {
  const theme = useTheme();

  return (
    <IPStack
      verticalAlign="center"
      styles={{ root: { padding: navCaptionPadding, height: navCaptionLineHeight, boxSizing: 'content-box' } }}
    >
      <div style={{ width: '100%', height: 1, backgroundColor: theme.semanticColors.bodyDivider }} />
    </IPStack>
  );
};
